import { useMemo, useState } from 'react'
import { Box, Tooltip } from '@mui/material'
import { TemplateOutputs } from 'Features/Projects/Scenarios/Templates/TemplateResults/TemplateResults.types'

/**
 * A plan template as a timeline, built the way the Scheduler's Factory
 * schedule is (SchedulerPortal `FactoryScheduleView.view.tsx`): plain DOM, no
 * charting library. One row per week; hours run left to right from 0 to the
 * larger of capacity and the busiest week; every segment is an absolutely
 * positioned Box whose `left` and `width` are percentages of that scale, so
 * the chart resizes with its container and needs no resize handling.
 *
 * Within a week, SKU groups run in production sequence, each as its
 * production time followed by its changeover and then its in-batch
 * changeover — the same order the stacked Plotly chart it replaces used.
 * Capacity is a dashed vertical line through every row.
 *
 * `mode="totalled"` collapses each week to three segments: all production,
 * all changeover, all in-batch changeover.
 */

type Kind = 'production' | 'changeover' | 'inBatch'

type Segment = {
  kind: Kind
  start: number
  hours: number
  /** The output row it came from; absent for a totalled segment. */
  row?: TemplateOutputs
  /** Position in the week's production sequence (1-based). */
  position?: number
}

type WeekRow = {
  week: number
  total: number
  production: number
  changeover: number
  segments: Segment[]
}

const KIND_LABEL: Record<Kind, string> = {
  production: 'Production',
  changeover: 'Changeover',
  inBatch: 'In-batch changeover',
}

// The Scheduler's gantt vocabulary: solid production, hatched changeovers.
const hatch = (colour: string) =>
  `repeating-linear-gradient(135deg, ${colour} 0 3px, var(--surface-0) 3px 5px)`

const KIND_FILL: Record<Kind, string> = {
  production: 'var(--chart-cat-1)',
  changeover: hatch('var(--gantt-changeover)'),
  inBatch: hatch('var(--gantt-preclean)'),
}

const ROW_H = 30
const LABEL_W = 56
const TOTAL_W = 104

const fmt = (hours: number) =>
  hours >= 10 ? hours.toFixed(1) : hours.toFixed(2)

/** The hours in a template's busiest week, to put several on one scale. */
export const busiestWeekHours = (outputs: TemplateOutputs[]) => {
  const totals = new Map<number, number>()
  outputs.forEach((r) =>
    totals.set(
      r.weekNo,
      (totals.get(r.weekNo) ?? 0) +
        (r.productionTimeInHrs || 0) +
        (r.changeOverTimeInHours || 0) +
        (r.inBatchChangeOverTimeInHours || 0)
    )
  )
  return Math.max(0, ...totals.values())
}

/** A round axis maximum and a tick step that gives 5–10 ticks. */
const scaleFor = (max: number) => {
  const steps = [1, 2, 5, 10, 20, 25, 50, 100]
  const step = steps.find((s) => max / s <= 10) ?? 100
  return { max: Math.max(step, Math.ceil(max / step) * step), step }
}

const buildRows = (
  outputs: TemplateOutputs[],
  mode: 'sequenced' | 'totalled'
): WeekRow[] => {
  const byWeek = new Map<number, TemplateOutputs[]>()
  outputs.forEach((row) => {
    const list = byWeek.get(row.weekNo) ?? []
    list.push(row)
    byWeek.set(row.weekNo, list)
  })

  return [...byWeek.entries()]
    .sort(([a], [b]) => a - b)
    .map(([week, rows]) => {
      const ordered = [...rows].sort(
        (a, b) => a.productionSequence - b.productionSequence
      )
      const production = ordered.reduce(
        (sum, r) => sum + (r.productionTimeInHrs || 0),
        0
      )
      const changeover = ordered.reduce(
        (sum, r) =>
          sum +
          (r.changeOverTimeInHours || 0) +
          (r.inBatchChangeOverTimeInHours || 0),
        0
      )

      const segments: Segment[] = []
      let cursor = 0
      const push = (seg: Omit<Segment, 'start'>) => {
        if (seg.hours > 0) {
          segments.push({ ...seg, start: cursor })
          cursor += seg.hours
        }
      }

      if (mode === 'totalled') {
        push({ kind: 'production', hours: production })
        push({
          kind: 'changeover',
          hours: ordered.reduce(
            (sum, r) => sum + (r.changeOverTimeInHours || 0),
            0
          ),
        })
        push({
          kind: 'inBatch',
          hours: ordered.reduce(
            (sum, r) => sum + (r.inBatchChangeOverTimeInHours || 0),
            0
          ),
        })
      } else {
        ordered.forEach((row, index) => {
          const position = index + 1
          push({
            kind: 'production',
            hours: row.productionTimeInHrs || 0,
            row,
            position,
          })
          push({
            kind: 'changeover',
            hours: row.changeOverTimeInHours || 0,
            row,
            position,
          })
          push({
            kind: 'inBatch',
            hours: row.inBatchChangeOverTimeInHours || 0,
            row,
            position,
          })
        })
      }

      return {
        week,
        total: production + changeover,
        production,
        changeover,
        segments,
      }
    })
}

const TipRow = ({ label, value }: { label: string; value: string }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
    <span>{label}</span>
    <Box component="span" sx={{ fontFamily: 'var(--font-mono)' }}>
      {value}
    </Box>
  </Box>
)

const SegmentTip = ({ seg, week }: { seg: Segment; week: number }) => (
  <Box sx={{ minWidth: 180, fontSize: 11.5, lineHeight: 1.5 }}>
    <Box sx={{ fontWeight: 700, mb: '2px' }}>
      {seg.row
        ? `${seg.row.skuGroupCode}${
            seg.row.skuGroupName ? ` · ${seg.row.skuGroupName}` : ''
          }`
        : `Week ${week} · ${KIND_LABEL[seg.kind]}`}
    </Box>
    <TipRow label={KIND_LABEL[seg.kind]} value={`${fmt(seg.hours)}h`} />
    <TipRow
      label="Runs"
      value={`${fmt(seg.start)}h – ${fmt(seg.start + seg.hours)}h`}
    />
    {seg.row && seg.position && (
      <TipRow label="Sequence" value={`#${seg.position} in week ${week}`} />
    )}
    {seg.row && seg.kind === 'production' && (
      <>
        <TipRow
          label="Quantity"
          value={`${seg.row.productionAmount?.toLocaleString() ?? '—'}${
            seg.row.unitOfMeasureCode ? ` ${seg.row.unitOfMeasureCode}` : ''
          }`}
        />
        <TipRow label="Cycle" value={`${seg.row.cycleTime ?? '—'} wk`} />
      </>
    )}
  </Box>
)

/** What the fills mean, and the capacity line (omitted with no capacity). */
export const TimelineLegend = ({ capacity }: { capacity?: number }) => (
  <Box
    sx={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: '14px',
      mb: '10px',
      fontSize: 11.5,
      color: 'var(--ink-600)',
    }}
  >
    {(Object.keys(KIND_LABEL) as Kind[]).map((kind) => (
      <Box
        key={kind}
        sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}
      >
        <Box
          sx={{
            width: 14,
            height: 10,
            borderRadius: 'var(--radius-sm)',
            background: KIND_FILL[kind],
            border:
              kind === 'production' ? 'none' : '1px solid var(--line-300)',
          }}
        />
        {KIND_LABEL[kind]}
      </Box>
    ))}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <Box sx={{ width: 14, borderTop: '2px dashed var(--brand-blue-600)' }} />
      {capacity === undefined ? 'Capacity' : `Capacity ${fmt(capacity)}h`}
    </Box>
  </Box>
)

export const TemplateTimeline = ({
  outputs,
  capacity,
  mode,
  maxHours,
  showLegend = true,
  focusSku: sharedFocus,
  onFocusSku,
}: {
  outputs: TemplateOutputs[]
  /** Hours available per week; drawn as the dashed capacity line. */
  capacity: number
  mode: 'sequenced' | 'totalled'
  /** Scale to at least this many hours, so timelines side by side line up. */
  maxHours?: number
  showLegend?: boolean
  /** A hovered SKU group shared between timelines (all fade the others). */
  focusSku?: string | null
  onFocusSku?: (sku: string | null) => void
}) => {
  const rows = useMemo(() => buildRows(outputs, mode), [outputs, mode])
  // Hovering a SKU group fades every other group, in every week, so one
  // group's place across the template reads at a glance.
  const [ownFocus, setOwnFocus] = useState<string | null>(null)
  const focusSku = onFocusSku ? (sharedFocus ?? null) : ownFocus
  const setFocusSku = onFocusSku ?? setOwnFocus

  const busiest = rows.reduce((max, r) => Math.max(max, r.total), 0)
  const { max: scaleMax, step } = scaleFor(
    Math.max(capacity, busiest, maxHours ?? 0)
  )
  const pct = (hours: number) => `${(hours / scaleMax) * 100}%`
  const ticks = Array.from(
    { length: Math.floor(scaleMax / step) + 1 },
    (_, i) => i * step
  )

  if (!rows.length) {
    return (
      <Box
        sx={{
          py: '32px',
          textAlign: 'center',
          fontSize: 12,
          color: 'var(--ink-500)',
        }}
      >
        No production in this template.
      </Box>
    )
  }

  return (
    <Box sx={{ minWidth: 0 }}>
      {showLegend && <TimelineLegend capacity={capacity} />}

      <Box
        role="img"
        aria-label={`Template by week: ${rows
          .map((r) => `week ${r.week} ${fmt(r.total)} hours`)
          .join(', ')}; capacity ${fmt(capacity)} hours`}
        sx={{
          display: 'grid',
          gridTemplateColumns: `${LABEL_W}px minmax(0, 1fr) ${TOTAL_W}px`,
          border: '1px solid var(--line-200)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
        }}
      >
        {/* Ruler */}
        <Box
          sx={{
            background: 'var(--panel-head)',
            borderBottom: '1px solid var(--line-300)',
            px: '10px',
            display: 'flex',
            alignItems: 'center',
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            color: 'var(--ink-500)',
          }}
        >
          Week
        </Box>
        <Box
          sx={{
            background: 'var(--panel-head)',
            borderBottom: '1px solid var(--line-300)',
            px: '8px',
          }}
        >
          <Box sx={{ position: 'relative', height: 26 }}>
            {ticks.map((t) => (
              <Box
                key={t}
                sx={{
                  position: 'absolute',
                  left: pct(t),
                  bottom: 4,
                  transform:
                    t === 0
                      ? 'none'
                      : t === scaleMax
                        ? 'translateX(-100%)'
                        : 'translateX(-50%)',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10,
                  color: 'var(--ink-500)',
                }}
              >
                {t}h
              </Box>
            ))}
          </Box>
        </Box>
        <Box
          sx={{
            background: 'var(--panel-head)',
            borderBottom: '1px solid var(--line-300)',
            px: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            color: 'var(--ink-500)',
          }}
        >
          Used
        </Box>

        {/* Weeks */}
        {rows.map((r, i) => {
          const over = r.total > capacity
          const used = capacity > 0 ? (r.total / capacity) * 100 : 0
          const stripe = i % 2 ? 'var(--surface-50)' : 'var(--surface-0)'
          return (
            <Box key={r.week} sx={{ display: 'contents' }}>
              <Box
                sx={{
                  background: stripe,
                  px: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--ink-700)',
                  borderBottom: '1px solid var(--line-100)',
                }}
              >
                W{r.week}
              </Box>
              <Box
                sx={{
                  position: 'relative',
                  height: ROW_H,
                  background: stripe,
                  borderBottom: '1px solid var(--line-100)',
                  px: '8px',
                }}
              >
                <Box sx={{ position: 'relative', height: '100%' }}>
                  {/* gridlines */}
                  {ticks.map((t) => (
                    <Box
                      key={t}
                      sx={{
                        position: 'absolute',
                        left: pct(t),
                        top: 0,
                        bottom: 0,
                        borderLeft: '1px solid var(--line-100)',
                      }}
                    />
                  ))}
                  {r.segments.map((seg, s) => {
                    const sku = seg.row?.skuGroupCode ?? null
                    const dim = focusSku !== null && sku !== focusSku
                    const wide = seg.hours / scaleMax >= 0.06
                    return (
                      <Tooltip
                        key={s}
                        title={<SegmentTip seg={seg} week={r.week} />}
                        placement="top"
                        enterDelay={150}
                        disableInteractive
                      >
                        <Box
                          onMouseEnter={() => sku && setFocusSku(sku)}
                          onMouseLeave={() => setFocusSku(null)}
                          sx={{
                            position: 'absolute',
                            left: pct(seg.start),
                            width: pct(seg.hours),
                            top: 4,
                            bottom: 4,
                            background: KIND_FILL[seg.kind],
                            boxShadow:
                              seg.kind === 'production'
                                ? 'inset -1px 0 0 rgba(255,255,255,0.6)'
                                : 'inset 0 0 0 1px var(--line-300)',
                            borderRadius: '1px',
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            px: wide ? '4px' : 0,
                            opacity: dim ? 0.25 : 1,
                            transition:
                              'opacity var(--motion-fast) var(--motion-easing)',
                            cursor: 'default',
                          }}
                        >
                          {wide && seg.kind === 'production' && (
                            <Box
                              component="span"
                              sx={{
                                fontFamily: 'var(--font-mono)',
                                fontSize: 10,
                                fontWeight: 600,
                                color: 'var(--surface-0)',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {sku ?? `${fmt(seg.hours)}h`}
                            </Box>
                          )}
                        </Box>
                      </Tooltip>
                    )
                  })}
                  {/* capacity */}
                  <Box
                    aria-hidden="true"
                    sx={{
                      position: 'absolute',
                      left: pct(capacity),
                      top: -1,
                      bottom: -1,
                      borderLeft: '2px dashed var(--brand-blue-600)',
                      zIndex: 2,
                      pointerEvents: 'none',
                    }}
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  background: stripe,
                  px: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: '6px',
                  borderBottom: '1px solid var(--line-100)',
                  fontFamily: 'var(--font-mono)',
                  fontVariantNumeric: 'tabular-nums',
                  fontSize: 11.5,
                  whiteSpace: 'nowrap',
                }}
              >
                <Box
                  component="span"
                  sx={{
                    color: over ? 'var(--state-failed-fg)' : 'var(--ink-900)',
                    fontWeight: 600,
                  }}
                >
                  {fmt(r.total)}h
                </Box>
                <Box
                  component="span"
                  sx={{
                    color: over ? 'var(--state-failed-fg)' : 'var(--ink-500)',
                  }}
                >
                  {used.toFixed(0)}%
                </Box>
              </Box>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}
